import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { LoginOutlined, PersonAddOutlined } from '@mui/icons-material';

function AuthScreen() {
  const theme = useTheme();
  const { loginWithRedirect } = useAuth0();

  const handleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'login'
      }
    });
  };

  const handleSignUp = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup'
      }
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 4, md: 6 },
            borderRadius: 3,
            backgroundColor: 'rgb(12, 19, 46)', // Dark purple background
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            textAlign: 'center'
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <img 
              src="/jurismap_logo.png" 
              alt="JurisMap"
              style={{
                height: '160px',
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto'
              }}
            />
          </Box>

          {/* Welcome Text */}
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              mb: 2,
              fontWeight: 600,
              color: '#ffffff' // White text on dark background
            }}
          >
            Bem-vindo ao JurisMap
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4,
              color: 'rgba(255, 255, 255, 0.8)', // Light white text for description
              lineHeight: 1.6,
              maxWidth: '400px',
              margin: '0 auto 32px auto'
            }}
          >
            Sua plataforma de pesquisa de precedentes jurídicos com IA. Acesse centenas de acórdãos, 
            converse com nossa IA especializada em processos do CRSNSP e crie defesas administrativas
            em segundos.
          </Typography>

          {/* Authentication Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PersonAddOutlined />}
              onClick={handleSignUp}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: 2,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[4]
                },
                transition: 'all 0.3s ease'
              }}
            >
              Criar Conta
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<LoginOutlined />}
              onClick={handleLogin}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: 2,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  borderColor: theme.palette.primary.main,
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[2]
                },
                transition: 'all 0.3s ease'
              }}
            >
              Fazer Login
            </Button>
          </Box>

          {/* Additional Info */}
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.6)', // Subtle white text for terms
              display: 'block',
              mt: 3
            }}
          >
            Ao continuar, você concorda com nossos Termos de Uso e Política de Privacidade
          </Typography>
        </Paper>

        {/* Features Preview */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3,
              color: theme.palette.text.primary,
              fontWeight: 500
            }}
          >
            O que você pode fazer no JurisMap
          </Typography>
          
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                🗺️ Mapear Acórdãos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pesquise visualmente acórdãos em um mapa interativo baseado em similaridade e com filtros dinâmicos.
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                💬 Chat com IA
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Converse com nossa IA especializada em acórdãos do CRSNSP para detalhes e linhas de argumentação.
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                ⚖️ Gerar Defesas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Crie documentos de defesa profissionais baseados em precedentes à sua escolha.
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default AuthScreen;